import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { SelectChangeEvent } from '@mui/material';
import { authService, USER_GROUPS, type User } from '../src/services/authService';
import { judgeService, JudgeUser } from '../src/services/judgeService';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const JudgeManagement: React.FC = () => {
  const [judgeUsers, setJudgeUsers] = useState<JudgeUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userLoading, setUserLoading] = useState(true);

  // Dialog states
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [selectedJudge, setSelectedJudge] = useState<JudgeUser | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  // Load current user
  useEffect(() => {
    const loadUser = async () => {
      try {
        setUserLoading(true);
        const user = await authService.getCurrentUser();
        setCurrentUser(user);
        console.log('JudgeManagement - Current user loaded:', user);
        console.log('JudgeManagement - User groups:', user.groups);
        console.log('JudgeManagement - Is SUPER_ADMIN:', authService.isInGroup(user, USER_GROUPS.SUPER_ADMIN));
      } catch (error) {
        console.error('Failed to load user in JudgeManagement:', error);
        setError('Failed to load user information');
      } finally {
        setUserLoading(false);
      }
    };

    loadUser();
  }, []);

  // Load judge users
  useEffect(() => {
    const loadJudgeUsers = async () => {
      if (currentUser && authService.isInGroup(currentUser, USER_GROUPS.SUPER_ADMIN)) {
        try {
          setLoading(true);
          setError('');
          const judgeUsers = await judgeService.getJudgeUsers();
          setJudgeUsers(judgeUsers);
        } catch (err) {
          console.error('Error loading judge users:', err);
          setError(`Failed to load judge users: ${err instanceof Error ? err.message : 'Unknown error'}`);
          setJudgeUsers([]);
        } finally {
          setLoading(false);
        }
      }
    };

    loadJudgeUsers();
  }, [currentUser]);

  // Check if user is super admin
  const isSuperAdmin = () => {
    return currentUser && authService.isInGroup(currentUser, USER_GROUPS.SUPER_ADMIN);
  };

  // Handle category assignment dialog
  const handleOpenAssignmentDialog = (judge: JudgeUser) => {
    setSelectedJudge(judge);
    setSelectedCategories([...judge.assignedCategories]);
    setAssignmentDialogOpen(true);
  };

  const handleCloseAssignmentDialog = () => {
    setAssignmentDialogOpen(false);
    setSelectedJudge(null);
    setSelectedCategories([]);
  };

  // Handle category selection change
  const handleCategoryChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setSelectedCategories(typeof value === 'string' ? value.split(',') : value);
  };

  // Handle save category assignment
  const handleSaveAssignment = async () => {
    if (!selectedJudge) return;

    try {
      setSaving(true);

      // Update the judge's assigned categories
      await judgeService.assignCategoriesToJudge(selectedJudge.email, selectedCategories, currentUser?.email || 'super-admin');

      // Update local state
      setJudgeUsers(prev => prev.map(judge =>
        judge.id === selectedJudge.id
          ? { ...judge, assignedCategories: selectedCategories }
          : judge
      ));

      handleCloseAssignmentDialog();
    } catch (err) {
      setError(`Failed to assign categories: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  // Get category label
  const getCategoryLabel = (categoryValue: string) => {
    const category = judgeService.getAvailableCategories().find(cat => cat.value === categoryValue);
    return category ? category.label : categoryValue;
  };

  // Show loading state
  if (userLoading || loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Check permissions
  if (!isSuperAdmin()) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Access denied. Only Super Admins can manage judge categories.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" mb={3}>
        <AssignmentIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4" component="h1">
          Judge Category Management
        </Typography>
      </Box>

      <Typography variant="body1" color="text.secondary" mb={3}>
        Assign award categories to judges. Judges will only see applications from their assigned categories.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Judge Email</TableCell>
                <TableCell>Full Name</TableCell>
                <TableCell>Assigned Categories</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {judgeUsers.length > 0 ? (
                judgeUsers.map((judge) => (
                  <TableRow key={judge.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {judge.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {judge.fullName || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" flexWrap="wrap" gap={1}>
                        {judge.assignedCategories.length > 0 ? (
                          judge.assignedCategories.map((category) => (
                            <Chip
                              key={category}
                              label={getCategoryLabel(category)}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No categories assigned
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={judge.isActive ? 'Active' : 'Inactive'}
                        color={judge.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Assign Categories">
                        <IconButton
                          onClick={() => handleOpenAssignmentDialog(judge)}
                          color="primary"
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No judge users found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Category Assignment Dialog */}
      <Dialog
        open={assignmentDialogOpen}
        onClose={handleCloseAssignmentDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Assign Categories to Judge
        </DialogTitle>
        <DialogContent>
          {selectedJudge && (
            <>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Judge: <strong>{selectedJudge.email}</strong>
              </Typography>
              <FormControl fullWidth>
                <InputLabel id="categories-label">Award Categories</InputLabel>
                <Select
                  labelId="categories-label"
                  multiple
                  value={selectedCategories}
                  onChange={handleCategoryChange}
                  input={<OutlinedInput label="Award Categories" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip
                          key={value}
                          label={getCategoryLabel(value)}
                          size="small"
                        />
                      ))}
                    </Box>
                  )}
                  MenuProps={MenuProps}
                >
                  {judgeService.getAvailableCategories().map((category) => (
                    <MenuItem key={category.value} value={category.value}>
                      <Checkbox checked={selectedCategories.indexOf(category.value) > -1} />
                      <ListItemText primary={category.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseAssignmentDialog}
            disabled={saving}
            startIcon={<CancelIcon />}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveAssignment}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {saving ? 'Saving...' : 'Save Assignment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default JudgeManagement;
